import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  CalendarIcon,
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  KeyIcon,
  EyeIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import useUserDetail from './useUserDetail';

export default function UserDetail() {
  const location = useLocation();
  const { userId } = location?.state || {};
  const urlParams = new URLSearchParams(location.search);
  const userIdFromUrl = urlParams.get('userId');
  const finalUserId = userId || userIdFromUrl;
  const navigate = useNavigate();
  const {
    user,
    loading,
    error,
    fetchUserDetail,
  } = useUserDetail();

  useEffect(() => {
    if (finalUserId) {
      fetchUserDetail(finalUserId);
    }
  }, [finalUserId, fetchUserDetail]);

  const formatCurrency = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-900 text-green-300';
      case 'inactive':
        return 'bg-red-900 text-red-300';
      case 'open':
        return 'bg-blue-900 text-blue-300';
      case 'closed':
        return 'bg-gray-900 text-gray-300';
      default:
        return 'bg-gray-900 text-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'inactive':
        return <XCircleIcon className="h-4 w-4" />;
      case 'open':
        return <ClockIcon className="h-4 w-4" />;
      case 'closed':
        return <XCircleIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  const viewTicket = (ticket) => {
    console.log('View ticket:', ticket);
    // TODO: Implement ticket detail view
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-white">Loading user details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-500">Error: {error}</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-400">User not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/Admin/Dashboard/Users')}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>Back to Users</span>
          </button>
        </div>
        <div className="text-sm text-gray-400">
          User ID: {finalUserId}
        </div>
      </div>

      {/* First Row: User Details and Subscription Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Details Box */}
        <div className="bg-blueGray border border-borderGray rounded-lg p-6">
          <h3 className="text-2xl font-bold text-white mb-4">User Details</h3>
   
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <UserIcon className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm text-gray-400">Name</div>
                <div className="text-white font-medium">{user.name || 'N/A'}</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <EnvelopeIcon className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm text-gray-400">Email</div>
                <div className="text-white font-medium">{user.email || 'N/A'}</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <CalendarIcon className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm text-gray-400">Join Date</div>
                <div className="text-white font-medium">{formatDate(user.createdAt)}</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <CreditCardIcon className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm text-gray-400">Stripe Customer ID</div>
                <div className="text-white font-mono text-sm">{user.stripeCustomerId || 'N/A'}</div>
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-400">Status</div>
              <div className="flex items-center space-x-2 mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getStatusColor(user.isActive ? 'active' : 'inactive')}`}>
                  {getStatusIcon(user.isActive ? 'active' : 'inactive')}
                  <span className="ml-1 capitalize">{user.isActive ? 'Active' : 'Inactive'}</span>
                </span>
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-400">Email Verified</div>
              <div className="text-white font-medium">{user.isVerified ? 'Yes' : 'No'}</div>
            </div>
          </div>
        </div>

        {/* Subscription Info Box */}
        <div className="bg-blueGray border border-borderGray rounded-lg p-6">
          <h3 className="text-2xl font-bold text-white mb-4">Subscription Info</h3>
          {user.activeSubscription ? (
            <div className="space-y-6">
              <div>
                <div className="text-sm text-gray-400">Plan Name</div>
                <div className="text-white font-medium text-lg">{user.activeSubscription.planId?.name || 'N/A'}</div>
              </div>
              <div className="flex items-center space-x-3">
                <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-400">Price</div>
                  <div className="text-white font-medium">{formatCurrency(user.activeSubscription.planId?.price || 0)}</div>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Billing Interval</div>
                <div className="text-white font-medium capitalize">{user.activeSubscription.planId?.interval || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Subscription Status</div>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getStatusColor(user.activeSubscription.status)}`}>
                    {getStatusIcon(user.activeSubscription.status)}
                    <span className="ml-1 capitalize">{user.activeSubscription.status || 'N/A'}</span>
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <CalendarIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-400">Current Period End</div>
                  <div className="text-white font-medium">{formatDate(user.activeSubscription.currentPeriodEnd)}</div>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Cancel at Period End</div>
                <div className="text-white font-medium">{user.activeSubscription.cancelAtPeriodEnd ? 'Yes' : 'No'}</div>
              </div>
            </div>
          ) : (
            <div className="text-gray-400">No active subscription</div>
          )}
        </div>
      </div>

      {/* API Keys Section */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-4">API Keys</h2>
        <div className="bg-blueGray border border-borderGray rounded-lg p-6">
          {user.apiKeys && user.apiKeys.length > 0 ? (
            <div className="space-y-4">
              {user.apiKeys.map((apiKey) => (
                <div key={apiKey._id} className="flex items-center justify-between p-4 bg-blueGray  border-b border-borderGray last:border-b-0">
                  <div className="flex items-center space-x-3">
                    <KeyIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="text-white font-medium">{apiKey.label || 'Unnamed Key'}</div>
                      <div className="text-gray-400 text-sm font-mono">
                        {apiKey.key ? `${apiKey.key.substring(0, 20)}...` : 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getStatusColor(apiKey.isActive ? 'active' : 'inactive')}`}>
                      {getStatusIcon(apiKey.isActive ? 'active' : 'inactive')}
                      <span className="ml-1 capitalize">{apiKey.isActive ? 'Active' : 'Inactive'}</span>
                    </span>
                    <div className="text-gray-400 text-sm">
                      Created: {formatDate(apiKey.createdAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-400 text-center py-8">No API keys found</div>
          )}
        </div>
      </div>

      {/* Support Tickets Section */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-4">Support Tickets</h2>
        <div className="bg-blueGray border border-borderGray rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-blueGray border-b border-borderGray">
                <tr>
                  <th className="px-6 py-4 text-left text-base font-medium text-gray-300 uppercase tracking-wider border-r border-borderGray">
                    Ticket ID
                  </th>
                  <th className="px-6 py-4 text-left text-base font-medium text-gray-300 uppercase tracking-wider border-r border-borderGray">
                    Subject
                  </th>
                  <th className="px-6 py-4 text-left text-base font-medium text-gray-300 uppercase tracking-wider border-r border-borderGray">
                    Status
                  </th>
                  {/* <th className="px-6 py-4 text-left text-base font-medium text-gray-300 uppercase tracking-wider">
                    Action
                  </th> */}
                </tr>
              </thead>
              <tbody className="divide-y divide-borderGray">
                {user.supportTickets && user.supportTickets.length > 0 ? (
                  user.supportTickets.map((ticket) => (
                    <tr key={ticket._id} className="hover:bg-gray-700/50">
                      <td className="px-6 py-4 whitespace-nowrap border-r border-borderGray">
                        <div className="flex items-center space-x-3">
                          <TicketIcon className="h-4 w-4 text-gray-400" />
                          <div>
                            <div className="text-sm text-white font-medium">
                              #{ticket._id.substring(0, 8)}
                            </div>
                            <div className="text-xs text-gray-400">
                              {formatDate(ticket.createdAt)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap border-r border-borderGray">
                        <div className="text-sm text-white font-medium">{ticket.subject || 'N/A'}</div>
                        <div className="text-xs text-gray-400 truncate max-w-xs">
                          {ticket.description || 'No description'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap border-r border-borderGray">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getStatusColor(ticket.status)}`}>
                          {getStatusIcon(ticket.status)}
                          <span className="ml-1 capitalize">{ticket.status}</span>
                        </span>
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => viewTicket(ticket)}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primaryBlue text-white hover:bg-primaryBlue/90"
                        >
                          <EyeIcon className="h-4 w-4" />
                          View
                        </button>
                      </td> */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-gray-400">
                      No support tickets found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
